import { useQuery, gql, NetworkStatus, useReactiveVar } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'
import { useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import Card from '@10xLabs/react-web/components/Card'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { SitesSalesHeader } from 'features/SitesSales/components/Header'
import { SitesSalesTabs } from 'features/SitesSales/components/Tabs'
import { SitesSalesContent } from 'features/SitesSales/components/Content'
import { DRAWER_STATUS } from 'features/SitesSales/components/DrawerStatus/constants'
import {
  Filters,
  FiltersWrapper,
  TIMEFRAMES_LIST,
  TIMEFRAMES,
  getTimeframeDates
} from 'features/SitesSales/components/Filters'
import { DrawersList } from './List'
import { DrawersPagination } from './Pagination'
import { hasAllAccess, canReadDrawerBookings, canReadDrawerSales, canReadDrawerKreditSales } from 'features/Users/utils'
import { getTimezone } from 'utils/date'
import { getQueryString, getQueries } from 'utils/route'
import { ReportToPrint } from 'features/SitesSales/components/ReportToPrint'
import { drawerToPrintVar } from 'variables/drawers'
import { useLogin } from 'features/Login/hooks'
import { IDrawersFilters } from 'features/SitesSales/types'
import { GTCompanySuggestion, GTUserSuggestion } from 'gql/types.generated'
import { useUserSuggestionsByIdsQuery } from 'features/Users/hooks'
import { useCompanySuggestionsByIdsQuery } from 'features/Companies/hooks'

const LIMIT = 30

const DRAWER_STATUS_LIST = [DRAWER_STATUS.OPEN, DRAWER_STATUS.CLOSED]

const NoPrint = styled.div<TSFixMe>`
  @media print {
    display: none;
  }
`

const Print = styled.div<TSFixMe>`
  display: none;

  @page {
    size: auto;
    margin: 0mm;
  }

  @media print {
    display: block;
  }
`

const PaginationContainer = styled.div<TSFixMe>`
  margin-top: 30px;
`

const messages = defineMessages({
  helmet: {
    id: 'drawers.helmet',
    defaultMessage: 'Drawers | Nexbus Admin'
  }
})

const GET_DRAWERS = gql`
  query GetDrawers(
    $limit: Int!
    $page: Int!
    $companyIds: [ID!]
    $siteIds: [ID!]
    $cashierIds: [ID!]
    $drawerStatus: DrawerStatus
    $from: String!
    $to: String!
    $timezone: String!
    $showBookings: Boolean!
    $showMobileSales: Boolean!
    $showSales: Boolean!
    $showKreditSales: Boolean!
  ) {
    drawers(
      pagination: { size: $limit, page: $page }
      input: {
        companyIDs: $companyIds
        siteIDs: $siteIds
        userIDs: $cashierIds
        status: $drawerStatus
        from: $from
        to: $to
        timezone: $timezone
      }
    ) {
      total
      items {
        user {
          id
          firstName
          lastName
          username
        }
        company {
          id
          name
        }
        site {
          id
          name
        }
        startTime
        endTime
        expectedAmount {
          bookings
          sales
          mobileSales
          kreditSales
        }
        actualAmount
        status
        drawerBookings @include(if: $showBookings) {
          id
          isCancelled
          buyer {
            firstName
            lastName
          }
          coupon {
            id
            code
          }
          tickets {
            id
            status
            passengerType
          }
          payments {
            id
            amount
            method
            status
            last4
            tickets {
              id
              subtotal
              vat
              discount
            }
          }
        }
        drawerSales @include(if: $showSales) {
          id
          payment {
            method
            amount
            last4
          }
        }
        drawerMobileSales @include(if: $showMobileSales) {
          id
          payment {
            method
            amount
            last4
          }
        }
        drawerKreditSales @include(if: $showKreditSales) {
          id
          payment {
            method
            amount
            last4
          }
        }
        receiptData {
          expectedAmount {
            bookings {
              cash
              card
            }
            sales {
              cash
              card
            }
            mobileSales {
              cash
              card
            }
            kreditSales {
              cash
              card
            }
          }
          actualAmount {
            cash
            card
          }
          ticketsByStatus {
            confirmed
            cancelled
          }
        }
      }
    }
  }
`

interface IDrawersResultProps {
  pageNb: number
  timeframe: string
  companyIds: string[]
  siteIds: string[]
  cashierIds: string[]
  drawerStatus: string
  filters: TSFixMe
}

const DrawersResult = ({
  pageNb,
  timeframe,
  companyIds,
  siteIds,
  cashierIds,
  drawerStatus,
  filters
}: IDrawersResultProps): TSFixMe => {
  const { from, to } = getTimeframeDates(timeframe)

  const login = useLogin()
  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
  } = useQuery(GET_DRAWERS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: LIMIT,
      page: pageNb - 1,
      companyIds,
      siteIds,
      cashierIds,
      drawerStatus: drawerStatus || null,
      from,
      to,
      timezone: getTimezone(),
      showBookings: canReadDrawerBookings(login.accessToken.policies),
      showSales: canReadDrawerSales(login.accessToken.policies),
      showMobileSales: canReadDrawerSales(login.accessToken.policies),
      showKreditSales: canReadDrawerKreditSales(login.accessToken.policies)
    }
  })

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (networkStatus === NetworkStatus.refetch || loading) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="drawers.error.description"
            defaultMessage="There was an error while loading your drawers"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="drawers.error.action" defaultMessage="Reload your drawers" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.drawers.items)) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="drawers.empty.description"
            defaultMessage="No drawers were found according to your filters"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else {
    return (
      <>
        <DrawersList drawers={data?.drawers?.items || []} />
        <PaginationContainer>
          <DrawersPagination
            page={pageNb}
            limit={LIMIT}
            totalItems={data.drawers.total}
            currentItemsCount={data.drawers.items.length}
            filters={filters}
          />
        </PaginationContainer>
      </>
    )
  }
}

const Drawers = (): TSFixMe => {
  const location = useLocation()
  const intl = useIntl()
  const navigate = useNavigate()
  const drawerToPrint = useReactiveVar(drawerToPrintVar)
  const login = useLogin()

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const {
    timeframe = '',
    companyIds = '',
    siteIds = '',
    cashierIds = '',
    drawerStatus = '',
    page = '1'
  } = getQueries(location.search)

  const pageNb = parseInt(page)
  const sanitizedTimeframe = TIMEFRAMES_LIST.includes(timeframe) ? timeframe : TIMEFRAMES.SEVEN_DAYS
  const sanitizedDrawerStatus = DRAWER_STATUS_LIST.indexOf(drawerStatus) > -1 ? drawerStatus : ''
  const companyIdsList = companyIds ? companyIds.split(',') : []
  const sanitizedSiteIds = siteIds ? siteIds.split(',') : []
  const cashierIdsList = cashierIds ? cashierIds.split(',') : []

  const { users } = useUserSuggestionsByIdsQuery({
    ids: cashierIdsList,
    skip: isEmpty(cashierIdsList)
  })
  const { companies } = useCompanySuggestionsByIdsQuery({
    ids: companyIdsList,
    skip: isEmpty(companyIdsList)
  })

  const filters: IDrawersFilters = {
    companies: !isEmpty(companies)
      ? companies
      : companyIdsList.map(
          (id: string): GTCompanySuggestion => ({
            id,
            name: '',
            vertical: ''
          })
        ),
    siteIds: sanitizedSiteIds,
    cashiers: !isEmpty(users)
      ? users
      : cashierIdsList.map(
          (id: string): GTUserSuggestion => ({
            id,
            firstName: '',
            lastName: '',
            name: '',
            username: ''
          })
        ),
    timeframe: sanitizedTimeframe,
    drawerStatus: sanitizedDrawerStatus
  }

  if (!hasAllAccess(FEATURES.SITE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SITE} />
  }

  const applyFilters = (values: IDrawersFilters): void => {
    const query = {
      companyIds: values.companies.map(({ id }) => id),
      siteIds: values.siteIds,
      cashierIds: values.cashiers.map(({ id }) => id),
      timeframe: values.timeframe,
      drawerStatus: values.drawerStatus
    }

    navigate(`/sites/sales/drawers?${getQueryString(query)}`)
  }

  return (
    <>
      <NoPrint>
        <SitesSalesHeader />
        <FiltersWrapper>
          <Filters filters={filters} applyFilters={applyFilters} showEmployees showDrawerStatus />
        </FiltersWrapper>
        <SitesSalesTabs />
        <SitesSalesContent>
          <Card.Container>
            <Card.Content noHeader noFooter>
              <DrawersResult
                pageNb={pageNb}
                timeframe={sanitizedTimeframe}
                companyIds={companyIdsList}
                siteIds={sanitizedSiteIds}
                cashierIds={cashierIdsList}
                drawerStatus={sanitizedDrawerStatus}
                filters={filters}
              />
            </Card.Content>
          </Card.Container>
        </SitesSalesContent>
      </NoPrint>
      <Print>
        {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
        <ReportToPrint drawerToPrint={drawerToPrint} />
      </Print>
    </>
  )
}

export default withLastUrl(Drawers)
